import React, { useState, useEffect } from 'react';
import { formatForDisplay, formatTimeOnly } from '../utils/timezone';
import TradeChartModal from './TradeChartModal';

function RecentTrades({ compact = false }) {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTrade, setExpandedTrade] = useState(null);
  const [reasoning, setReasoning] = useState({});
  const [loadingReasoning, setLoadingReasoning] = useState({});
  const [chartModal, setChartModal] = useState({ isOpen: false, tradeId: null });

  useEffect(() => {
    const fetchTrades = async () => {
      try {
        const res = await fetch('/api/trades/recent?limit=10');
        if (res.ok) {
          const data = await res.json();
          setTrades(data);
        }
      } catch (err) {
        console.error('Error fetching recent trades:', err);
      }
      setLoading(false);
    };

    fetchTrades();
    // Refresh every 30 seconds
    const interval = setInterval(fetchTrades, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleViewDetails = async (tradeId) => {
    if (expandedTrade === tradeId) {
      setExpandedTrade(null);
      return;
    }

    setExpandedTrade(tradeId);

    if (!reasoning[tradeId]) {
      setLoadingReasoning(prev => ({ ...prev, [tradeId]: true }));
      try {
        const res = await fetch(`/api/trades/${tradeId}/reasoning`);
        if (res.ok) {
          const data = await res.json();
          setReasoning(prev => ({ ...prev, [tradeId]: data }));
        }
      } catch (err) {
        console.error('Error fetching trade reasoning:', err);
      }
      setLoadingReasoning(prev => ({ ...prev, [tradeId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-pulse text-dark-text-secondary">Loading recent trades...</div>
      </div>
    );
  }

  if (trades.length === 0) {
    return (
      <div className="flex items-center justify-center py-10 px-6 text-dark-text-muted text-sm">
        No completed trades yet
      </div>
    );
  }

  // Compact mode for sidebar
  if (compact) {
    return (
      <div className="divide-y divide-dark-border-subtle">
        {trades.slice(0, 5).map((trade) => (
          <div key={trade.id} className="px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ProviderDot provider={trade.provider} />
              <span className="text-xs text-dark-text-secondary">{trade.model_name}</span>
            </div>
            <span className={`font-mono text-xs font-medium ${
              (trade.pnl_points || 0) >= 0 ? 'text-positive' : 'text-negative'
            }`}>
              ${(trade.pnl_points || 0) >= 0 ? '+' : ''}{trade.pnl_points?.toFixed(2)}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="card overflow-hidden">
        <div className="divide-y divide-dark-border-subtle">
          {trades.map((trade) => (
            <TradeRow
              key={trade.id}
              trade={trade}
              isExpanded={expandedTrade === trade.id}
              reasoning={reasoning[trade.id]}
              loadingReasoning={loadingReasoning[trade.id]}
              onViewDetails={() => handleViewDetails(trade.id)}
              onOpenChart={(tradeId) => setChartModal({ isOpen: true, tradeId })}
            />
          ))}
        </div>
      </div>

      <TradeChartModal
        isOpen={chartModal.isOpen}
        onClose={() => setChartModal({ isOpen: false, tradeId: null })}
        tradeId={chartModal.tradeId}
      />
    </>
  );
}

function TradeRow({ trade, isExpanded, reasoning, loadingReasoning, onViewDetails, onOpenChart }) {
  const isProfit = (trade.pnl_usd || trade.pnl_points || 0) >= 0;
  const pnlDisplay = trade.pnl_usd != null ? trade.pnl_usd : trade.pnl_points;
  const pnlPrefix = trade.pnl_usd != null ? '$' : '';

  // Calculate trade duration
  const getDuration = () => {
    if (!trade.entry_time || !trade.exit_time) return null;
    const entry = new Date(trade.entry_time);
    const exit = new Date(trade.exit_time);
    const diffMs = exit - entry;
    const diffMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  // Calculate price change
  const priceChange = trade.exit_price && trade.entry_price
    ? (trade.exit_price - trade.entry_price).toFixed(1)
    : null;

  // Distance from entry to SL (always negative/risk)
  const getSlDistanceFromEntry = () => {
    if (!trade.stop_loss || !trade.entry_price) return '0.0';
    const distance = Math.abs(trade.entry_price - trade.stop_loss);
    return `-${distance.toFixed(1)}`;
  };

  // Distance from entry to TP (always positive/reward)
  const getTpDistanceFromEntry = () => {
    if (!trade.take_profit || !trade.entry_price) return '0.0';
    const distance = Math.abs(trade.take_profit - trade.entry_price);
    return distance.toFixed(1);
  };

  // Risk:Reward ratio
  const getRiskRewardRatio = () => {
    if (!trade.stop_loss || !trade.take_profit || !trade.entry_price) return 1;
    const risk = Math.abs(trade.entry_price - trade.stop_loss);
    const reward = Math.abs(trade.take_profit - trade.entry_price);
    return risk > 0 ? reward / risk : 1;
  };

  return (
    <div
      className={`transition-all cursor-pointer border-l-4 border-l-transparent hover:bg-dark-tertiary ${
        isProfit ? 'hover:border-l-positive' : 'hover:border-l-negative'
      } ${isExpanded ? 'bg-dark-tertiary ' + (isProfit ? 'border-l-positive' : 'border-l-negative') : ''}`}
      onClick={onViewDetails}
    >
      {/* Main Row */}
      <div className="px-3 py-2.5 sm:px-5 sm:py-3">
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Model Info */}
          <div className="min-w-0 w-[90px] sm:w-auto sm:flex-1">
            <div className="font-medium text-xs sm:text-sm text-dark-text truncate">{trade.model_name}</div>
            <div className="text-[10px] sm:text-xs text-dark-text-muted">
              {formatTimeRangeMobile(trade.entry_time, trade.exit_time)}
            </div>
          </div>

          {/* Direction Arrow Only on Mobile */}
          <div className={`flex items-center justify-center w-6 h-6 sm:w-auto sm:h-auto sm:gap-1 sm:px-2.5 sm:py-1 rounded text-[11px] font-semibold ${
            trade.direction === 'LONG'
              ? 'bg-positive/20 text-positive'
              : 'bg-negative/20 text-negative'
          }`}>
            {trade.direction === 'LONG' ? (
              <svg className="w-3.5 h-3.5 sm:w-3 sm:h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
              </svg>
            ) : (
              <svg className="w-3.5 h-3.5 sm:w-3 sm:h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            )}
            <span className="hidden sm:inline">{trade.direction}</span>
          </div>

          {/* Symbol Badge */}
          <SymbolBadge symbol={trade.symbol} />

          {/* Live execution badge */}
          {trade.execution_mode === 'LIVE' && <LiveBadge status={trade.live_status} />}

          {/* Chart Icon */}
          {trade.chart_path && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onOpenChart(trade.id);
              }}
              className="p-1 sm:p-1.5 rounded hover:bg-dark-surface text-dark-text-muted hover:text-dark-text transition-colors"
              title="View trade chart"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </button>
          )}

          {/* Price Movement */}
          <div className="hidden md:flex items-center gap-2 flex-1 justify-center">
            <span className="font-mono text-sm text-dark-text-secondary">{trade.entry_price?.toFixed(1)}</span>
            <span className="text-dark-text-muted">→</span>
            <span className="font-mono text-sm text-dark-text">{trade.exit_price?.toFixed(1)}</span>
            {priceChange && (
              <span className={`font-mono text-sm font-medium ${isProfit ? 'text-positive' : 'text-negative'}`}>
                {isProfit ? '+' : ''}{Math.abs(parseFloat(priceChange)).toFixed(1)}
              </span>
            )}
          </div>

          {/* P/L - Prominent */}
          <div className={`font-mono font-bold text-sm sm:text-base text-right flex-1 sm:flex-none sm:w-[100px] ${
            isProfit ? 'text-positive' : 'text-negative'
          }`}>
            {pnlPrefix}{isProfit ? '+' : ''}{pnlDisplay?.toFixed(2)}
          </div>

          {/* Chevron */}
          <div className="text-dark-text-muted">
            <svg className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Expandable Details Section */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isExpanded ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pb-5 pt-2">
          {/* Trade Chart */}
          {trade.chart_path && isExpanded && (
            <div className="mb-4 flex justify-center">
              <img
                src={`/api/trades/${trade.id}/chart`}
                alt="Trade chart"
                className="max-w-full h-auto rounded-lg border border-dark-border cursor-pointer hover:opacity-90 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenChart(trade.id);
                }}
              />
            </div>
          )}

          {/* Brief Reasoning */}
          {(loadingReasoning || reasoning?.entry?.trade_reasoning) && (
            <div className="p-3 bg-dark-surface rounded-lg border border-dark-border-subtle">
              {loadingReasoning ? (
                <div className="animate-pulse">
                  <div className="h-3 bg-gray-700 rounded w-3/4"></div>
                </div>
              ) : (
                <p className="text-sm text-dark-text-secondary leading-relaxed">
                  {reasoning?.entry?.trade_reasoning}
                </p>
              )}
            </div>
          )}

          {/* Two-column layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-5">
            {/* Risk Management Column */}
            <div>
              <div className="text-xs uppercase tracking-wider text-dark-text-muted border-b border-dark-border pb-2 mb-3">
                Risk Management
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-dark-text-secondary">Stop Loss Distance</span>
                  <span className="font-mono text-sm text-negative">
                    {getSlDistanceFromEntry()} pts
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-dark-text-secondary">Take Profit Distance</span>
                  <span className="font-mono text-sm text-positive">
                    +{getTpDistanceFromEntry()} pts
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-dark-text-secondary">Risk : Reward</span>
                  <div className="flex items-center gap-2">
                    <RiskRewardBar ratio={getRiskRewardRatio()} />
                    <span className="font-mono text-sm text-dark-text">
                      1:{getRiskRewardRatio().toFixed(1)}
                    </span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-dark-text-secondary">Exit Reason</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${getExitReasonColor(trade.exit_reason)}`}>
                    {formatExitReason(trade.exit_reason)}
                  </span>
                </div>
              </div>
            </div>

            {/* Trade Results Column */}
            <div>
              <div className="text-xs uppercase tracking-wider text-dark-text-muted border-b border-dark-border pb-2 mb-3">
                Trade Results
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-dark-text-secondary">Entry → Exit</span>
                  <span className="font-mono text-sm text-dark-text">
                    {trade.entry_price?.toFixed(1)} → {trade.exit_price?.toFixed(1)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-dark-text-secondary">P/L (points)</span>
                  <span className={`font-mono text-sm ${isProfit ? 'text-positive' : 'text-negative'}`}>
                    {isProfit ? '+' : ''}{trade.pnl_points?.toFixed(1)} pts
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-dark-text-secondary">P/L (USD)</span>
                  <span className={`font-mono text-sm ${isProfit ? 'text-positive' : 'text-negative'}`}>
                    {isProfit ? '+' : ''}${trade.pnl_usd?.toFixed(0) || '—'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-dark-text-secondary">Duration</span>
                  <span className="font-mono text-sm text-dark-text">{getDuration() || '—'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Live Execution details (real broker fill, costs, ticket) */}
          {trade.execution_mode === 'LIVE' && <LiveExecutionDetails trade={trade} />}

          {/* Detailed Reasoning Sections */}
          <div className="mt-5">
            <TradeReasoningDisplay
              reasoning={reasoning}
              loading={loadingReasoning}
              trade={trade}
              hideBriefReasoning={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function formatExitReason(reason) {
  switch (reason) {
    case 'TP_HIT': return 'TP Hit';
    case 'SL_HIT': return 'SL Hit';
    case 'LLM_CLOSE': return 'LLM Close';
    case 'SESSION_END': return 'Session End';
    case 'MANUAL': return 'Manual';
    case 'BROKER_CLOSE': return 'Broker Close';
    case 'MONTH_END': return 'Month End';
    case 'LLM_FLIP': return 'LLM Flip';
    default: return reason;
  }
}

// Live trading badge: shows a model is executing on the real MT4 account.
function LiveBadge({ status }) {
  // Highlight non-OPEN/CLOSED states (rejected/pending) so problems stand out.
  const isAlert = status && !['OPEN', 'CLOSED'].includes(status);
  const color = isAlert
    ? 'bg-negative/20 text-negative'
    : 'bg-accent-gold/20 text-accent-gold';
  return (
    <div className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold ${color}`} title={status ? `Live status: ${status}` : 'Live trade'}>
      <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
      LIVE
    </div>
  );
}

// Detailed real-broker execution info (fill price, lots, costs, ticket).
function LiveExecutionDetails({ trade }) {
  const fmt = (v, digits = 2) => (v != null && !Number.isNaN(Number(v)) ? Number(v).toFixed(digits) : '—');
  const commission = trade.broker_commission;
  const swap = trade.broker_swap;
  // Real-money result from the broker's raw profit when recorded. Legacy rows
  // (before the broker_profit column) stored broker-net in pnl_usd, so fall
  // back to deriving from it. pnl_usd itself is now the sim-scaled BENCHMARK
  // P/L on live trades (leaderboard-comparable), shown as its own row.
  const grossUsd = trade.broker_profit != null
    ? trade.broker_profit
    : (trade.pnl_usd != null ? trade.pnl_usd - (commission || 0) - (swap || 0) : null);
  const netUsd = trade.broker_profit != null
    ? trade.broker_profit + (commission || 0) + (swap || 0)
    : trade.pnl_usd;

  const Row = ({ label, value, mono = true, className = '' }) => (
    <div className="flex justify-between items-center">
      <span className="text-sm text-dark-text-secondary">{label}</span>
      <span className={`${mono ? 'font-mono ' : ''}text-sm text-dark-text ${className}`}>{value}</span>
    </div>
  );

  return (
    <div className="mt-5 p-4 rounded-lg border border-accent-gold/30 bg-accent-gold/5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-accent-gold border-b border-accent-gold/20 pb-2 mb-3">
        <span className="w-1.5 h-1.5 rounded-full bg-accent-gold animate-pulse" />
        Live Execution (real broker fill)
      </div>

      {trade.live_error && (
        <div className="mb-3 p-2 rounded bg-negative/10 border border-negative/30 text-xs text-negative font-mono break-words">
          {trade.live_error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
        <Row label="Broker fill (entry)" value={fmt(trade.broker_open_price ?? trade.entry_price, 1)} />
        <Row label="Exit fill" value={fmt(trade.exit_price, 1)} />
        <Row label="Lots" value={fmt(trade.lots, 2)} />
        <Row label="Broker ticket" value={trade.broker_ticket || '—'} />
        <Row label="Commission" value={commission != null ? `$${fmt(commission)}` : '—'}
          className={commission < 0 ? 'text-negative' : ''} />
        <Row label="Swap" value={swap != null ? `$${fmt(swap)}` : '—'}
          className={swap < 0 ? 'text-negative' : ''} />
        <Row label="Gross P/L" value={grossUsd != null ? `${grossUsd >= 0 ? '+' : ''}$${fmt(grossUsd)}` : '—'}
          className={grossUsd >= 0 ? 'text-positive' : 'text-negative'} />
        <Row label="Net P/L (after costs)" value={netUsd != null ? `${netUsd >= 0 ? '+' : ''}$${fmt(netUsd)}` : '—'}
          className={netUsd >= 0 ? 'text-positive' : 'text-negative'} />
        {trade.broker_profit != null && trade.pnl_usd != null && (
          <Row label="Benchmark P/L (sim)" value={`${trade.pnl_usd >= 0 ? '+' : ''}$${fmt(trade.pnl_usd)}`}
            className={trade.pnl_usd >= 0 ? 'text-positive' : 'text-negative'} />
        )}
      </div>

      {trade.live_status && (
        <div className="mt-3 pt-3 border-t border-accent-gold/20 flex justify-between items-center">
          <span className="text-sm text-dark-text-secondary">Live status</span>
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-dark-tertiary text-dark-text border border-dark-border">
            {trade.live_status}
          </span>
        </div>
      )}
    </div>
  );
}

function RiskRewardBar({ ratio }) {
  // ratio is the reward:risk ratio (e.g., 1.4 means 1:1.4)
  // Total is 1 (risk) + ratio (reward)
  const total = 1 + ratio;
  const riskPercent = (1 / total) * 100;
  const rewardPercent = (ratio / total) * 100;

  return (
    <div className="flex h-2 w-16 rounded-sm overflow-hidden">
      <div
        className="bg-negative"
        style={{ width: `${riskPercent}%` }}
      />
      <div
        className="bg-positive"
        style={{ width: `${rewardPercent}%` }}
      />
    </div>
  );
}

function SymbolBadge({ symbol, className = '' }) {
  // Format symbol for display
  const getSymbolInfo = (sym) => {
    switch (sym) {
      case '.DE40.':
        return { label: 'DAX', color: 'bg-blue-500/20 text-blue-400' };
      case '.USTEC.':
        return { label: 'NAS', color: 'bg-orange-500/20 text-orange-400' };
      case '.UK100.':
        return { label: 'FTSE', color: 'bg-red-500/20 text-red-400' };
      case '.US30.':
        return { label: 'DOW', color: 'bg-cyan-500/20 text-cyan-400' };
      default:
        return { label: 'US100', color: 'bg-orange-500/20 text-orange-400' };
    }
  };

  const { label, color } = getSymbolInfo(symbol);

  return (
    <div className={`px-2.5 py-1 rounded text-[11px] font-semibold ${color} ${className}`}>
      {label}
    </div>
  );
}

function TradeReasoningDisplay({ reasoning, loading, trade, hideBriefReasoning = false }) {
  if (loading) {
    return null; // Loading state handled above
  }

  const hasSlTpReasoning = reasoning?.entry?.sl_reasoning || reasoning?.entry?.tp_reasoning;
  const hasExitReasoning = reasoning?.exit?.trade_reasoning || reasoning?.exit?.reasoning;

  // If hideBriefReasoning is true and we only have trade_reasoning, don't show anything
  if (hideBriefReasoning && !hasSlTpReasoning && !hasExitReasoning) {
    return null;
  }

  if (!hasSlTpReasoning && !hasExitReasoning) {
    return null;
  }

  return (
    <div className="space-y-3">
      {/* Entry Reasoning - only show SL/TP reasoning if hideBriefReasoning is true */}
      {reasoning?.entry && hasSlTpReasoning && (
        <div className="bg-dark-tertiary rounded-lg p-4">
          <h4 className="text-xs font-semibold text-accent-gold uppercase tracking-wider mb-3">
            Entry Decision
          </h4>
          <div className="space-y-3">
            {reasoning.entry.sl_reasoning && (
              <div>
                <h5 className="text-xs font-medium text-negative mb-1">Stop Loss Reasoning</h5>
                <p className="text-sm text-dark-text leading-relaxed">
                  {reasoning.entry.sl_reasoning}
                </p>
              </div>
            )}
            {reasoning.entry.tp_reasoning && (
              <div>
                <h5 className="text-xs font-medium text-positive mb-1">Take Profit Reasoning</h5>
                <p className="text-sm text-dark-text leading-relaxed">
                  {reasoning.entry.tp_reasoning}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Exit Reasoning */}
      {reasoning?.exit && (
        <div className="bg-dark-tertiary rounded-lg p-4">
          <h4 className="text-xs font-semibold text-dark-text-secondary uppercase tracking-wider mb-3">
            Exit Decision ({trade.exit_reason})
          </h4>
          <div className="space-y-3">
            {reasoning.exit.trade_reasoning && (
              <div>
                <h5 className="text-xs font-medium text-dark-text-secondary mb-1">Exit Rationale</h5>
                <p className="text-sm text-dark-text leading-relaxed">
                  {reasoning.exit.trade_reasoning}
                </p>
              </div>
            )}
            {reasoning.exit.reasoning && !reasoning.exit.trade_reasoning && (
              <div>
                <h5 className="text-xs font-medium text-dark-text-secondary mb-1">Reason</h5>
                <p className="text-sm text-dark-text leading-relaxed">
                  {reasoning.exit.reasoning}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ProviderDot({ provider }) {
  const colors = {
    'Anthropic': 'bg-orange-500',
    'OpenAI': 'bg-emerald-500',
    'Google': 'bg-blue-500',
    'xAI': 'bg-purple-500'
  };

  return (
    <div className={`w-2 h-2 rounded-full ${colors[provider] || 'bg-gray-500'}`} />
  );
}

function formatTime(isoString) {
  if (!isoString) return '';
  // Use UTC for display
  return formatForDisplay(isoString, {
    includeDate: true,
    includeTime: true,
    includeTimezone: true,
    shortDate: true
  });
}

function formatTimeRange(entryTime, exitTime) {
  if (!entryTime || !exitTime) return '';

  const entry = new Date(entryTime);
  const exit = new Date(exitTime);

  const entryDate = `${entry.getUTCMonth() + 1}/${entry.getUTCDate()}`;
  const exitDate = `${exit.getUTCMonth() + 1}/${exit.getUTCDate()}`;
  const entryHours = entry.getUTCHours().toString().padStart(2, '0');
  const entryMins = entry.getUTCMinutes().toString().padStart(2, '0');
  const exitHours = exit.getUTCHours().toString().padStart(2, '0');
  const exitMins = exit.getUTCMinutes().toString().padStart(2, '0');

  // Show date on both if different days
  if (entryDate !== exitDate) {
    return `${entryDate} ${entryHours}:${entryMins} → ${exitDate} ${exitHours}:${exitMins} UTC`;
  }

  return `${entryDate} ${entryHours}:${entryMins} → ${exitHours}:${exitMins} UTC`;
}

// Shorter format for mobile: "1/8 17:01" (entry time only, no UTC)
function formatTimeRangeMobile(entryTime, exitTime) {
  if (!entryTime) return '';

  const entry = new Date(entryTime);
  const entryDate = `${entry.getUTCMonth() + 1}/${entry.getUTCDate()}`;
  const entryHours = entry.getUTCHours().toString().padStart(2, '0');
  const entryMins = entry.getUTCMinutes().toString().padStart(2, '0');

  return `${entryDate}, ${entryHours}:${entryMins}`;
}

function getExitReasonColor(reason) {
  switch (reason) {
    case 'TP_HIT':
      return 'bg-positive/10 text-positive border border-positive/30';
    case 'SL_HIT':
      return 'bg-negative/10 text-negative border border-negative/30';
    case 'LLM_CLOSE':
      return 'bg-accent-gold/10 text-accent-gold border border-accent-gold/30';
    case 'SESSION_END':
      return 'bg-dark-tertiary text-dark-text-muted border border-dark-border';
    case 'MANUAL':
      return 'bg-purple-500/10 text-purple-400 border border-purple-500/30';
    case 'BROKER_CLOSE':
      return 'bg-accent-gold/10 text-accent-gold border border-accent-gold/30';
    default:
      return 'bg-dark-tertiary text-dark-text-muted border border-dark-border';
  }
}

export default RecentTrades;
